import { ComponentChild } from 'preact'
import { PureComponent } from 'preact-compat'

import { getInsidedData } from '@insided/destination/utils/insidedDataStorage'
import { convert, getContentType } from '../services/topicService'
import { ParentCategory } from '../interfaces'
import {
  CONTENT_TYPE_ARTICLE,
  CONTENT_TYPE_IDEA,
  CONTENT_TYPE_PRODUCT_UPDATE,
  CONTENT_TYPE_QUESTION,
  ContentType,
  getConsistentContentType
} from '@insided/shared/utils/contentType'

/* eslint-disable import/no-internal-modules */
import tracker from '../../../../../components/tracker/publish'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@insided/deprecated-preact-components/modal'
import ButtonControl from '@insided/deprecated-preact-components/button-control'
import InputGroup from '@insided/deprecated-control/ui-components/InputGroup'
import CategorySelect from '@insided/deprecated-control/category-select/CategorySelect'
import { common } from '@insided/deprecated-preact-components/common'
import { addSpacesAndLowerCase, getIndefiniteArticle } from '@insided/deprecated-preact-components/utils/string'

interface Props {
  topicId: number
  publicId: number
  fromContentType: ContentType
  toContentType: ContentType
  categories?: ParentCategory[]
  onShowAlert: (type: 'success' | 'error', message?: string) => void
  hideModal: () => void
  setMessage: (message?: ComponentChild) => void
}

interface State {
  categoryId: number | null
  errorMessage?: string
  isLoading: boolean
}

const additionalDescriptions = {
  [CONTENT_TYPE_IDEA]:
    'Please note that idea properties will be lost during this conversion, e.g. pinned reply, ideation status, product areas.',
  [CONTENT_TYPE_QUESTION]:
    'Please note that question properties will be lost during this conversion, e.g. best answer.',
  [CONTENT_TYPE_ARTICLE]:
    'Please note that some article properties will be lost during this conversion, e.g. featured image, article label, version history.'
}

const contentTypesWithoutCategory = [CONTENT_TYPE_IDEA, CONTENT_TYPE_PRODUCT_UPDATE]

class ConvertTopicModal extends PureComponent<Props, State> {
  private readonly isKnowledgeBaseEnabled = common.isModuleActive('knowledgeBase')
  private readonly isGroupsEnabled = common.isModuleActive('group')

  constructor() {
    super()
    this.state = {
      categoryId: null,
      isLoading: false
    }
    this.onCategorySelected = this.onCategorySelected.bind(this)
    this.convertTopic = this.convertTopic.bind(this)
  }

  onCategorySelected(categoryId: number | undefined) {
    this.setState({
      categoryId: categoryId ?? null,
      errorMessage: undefined
    })
  }

  async convertTopic() {
    const { topicId, fromContentType, toContentType, categories, onShowAlert } = this.props
    const { categoryId } = this.state

    if (categories && contentTypesWithoutCategory.includes(fromContentType) && !categoryId) {
      this.setState({
        errorMessage: `Please select a category${this.isGroupsEnabled ? ' or group' : ''}`
      })
      return
    }

    this.setState({ isLoading: true })

    try {
      await convert(fromContentType, topicId, toContentType, categoryId ?? undefined)

      tracker.trackEvent('userevent', {
        type: 'Topic converted',
        'Converted from content type': fromContentType,
        'Converted to content type': toContentType,
        event_source: 'control',
        userid: getInsidedData('user').userid
      })

      await this.redirectAfterConversion()
    } catch {
      onShowAlert('error')
      this.setState({ isLoading: false })
    }
  }

  async redirectAfterConversion() {
    const { toContentType, publicId, onShowAlert, setMessage, hideModal } = this.props
    let hasError = false

    for (let tryCount = 0; tryCount < 10; tryCount++) {
      try {
        const newContentType = getConsistentContentType(await getContentType(publicId))
        if (newContentType === toContentType) {
          this.setState({ isLoading: false })
          window.location.reload()
          return
        } else {
          await new Promise((resolve) => window.setTimeout(resolve, 1000))
        }
      } catch {
        hasError = true
        break
      }
    }

    if (hasError) {
      onShowAlert('error')
      this.setState({ isLoading: false })
    } else {
      setMessage(
        <div>
          Sorry, the topic conversion is taking a bit longer than expected. Please try <a href=''>refreshing</a> the
          page.
        </div>
      )
      hideModal()
    }
  }

  render() {
    const { fromContentType, toContentType, hideModal, categories } = this.props
    const { categoryId, errorMessage, isLoading } = this.state
    const formattedFrom = addSpacesAndLowerCase(fromContentType)
    const formattedTo = addSpacesAndLowerCase(toContentType)

    return (
      <Modal isOpen size='medium'>
        <ModalHeader title={`Convert ${formattedFrom} to ${formattedTo}`} onClose={hideModal} />
        <ModalBody>
          <div className='convert-topic-description'>
            <p>
              Are you sure that you want to convert this {formattedFrom} to {getIndefiniteArticle(toContentType)}{' '}
              {formattedTo}?
            </p>
            {fromContentType in additionalDescriptions && (
              <p>{additionalDescriptions[fromContentType as 'idea' | 'question' | 'article']}</p>
            )}
            {categories && contentTypesWithoutCategory.includes(fromContentType) && (
              <InputGroup
                title='Publish the converted topic in'
                error={errorMessage}
                customInput={
                  <CategorySelect
                    onUpdate={this.onCategorySelected}
                    categories={categories}
                    forum_id={categoryId}
                    error={errorMessage}
                    isKnowledgeBaseEnabled={this.isKnowledgeBaseEnabled}
                    isGroupsEnabled={this.isGroupsEnabled}
                    hideLabel
                    newStyle
                  />
                }
                showIconBeforeErrorMessage
              />
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <ButtonControl
            title='Convert'
            onClick={this.convertTopic}
            variant='brand'
            newStyle
            size='medium'
            isLoading={isLoading}
            // eslint-disable-next-line @gorazdo/preact/prefer-classname
            class='qa-convert-topic'
          />
          <ButtonControl title='Cancel' onClick={hideModal} newStyle size='medium' />
        </ModalFooter>
      </Modal>
    )
  }
}

export default ConvertTopicModal
